import React, { useState } from 'react';
import { Backdrop, Grow, Modal } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import swal2 from 'sweetalert2';
import * as Utils from '../../commons/utils/Utils';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';

const escapeHtml = (texto) => String(texto)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// VEC-4367: justificación masiva — mismo motivo (obligatorio) y misma fecha de retorno (opcional)
// para los registros seleccionados en la página. El backend omite los que ya no se pueden
// justificar (plazo vencido, etc.) y los informa; los demás quedan auditados uno por uno.
const DisponibilidadActivaJustificarMasivoModal = ({ open, seleccionados, comboMotivos, handleClose, onJustificados }) => {
  const intl = useIntl();
  const [motivoId, setMotivoId] = useState('');
  const [fechaRetorno, setFechaRetorno] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const dominios = seleccionados.map((m) => m.dominio);
  const vehiculos = new Set(seleccionados.map((m) => m.movil_id)).size;
  // Ninguna fecha anterior al inicio de algún registro seleccionado: el backend la rechazaría para ese registro.
  const inicioMasReciente = seleccionados.reduce((max, m) => (m.fechaInicioOciosidad > max ? m.fechaInicioOciosidad : max), '');

  const cerrar = () => {
    setMotivoId('');
    setFechaRetorno(null);
    handleClose();
  };

  const textoOmitido = (motivo) => {
    switch (motivo) {
      case 'plazo_vencido':
        return intl.formatMessage({ id: 'disponibilidadActiva.masivo.omitido_plazo_vencido', defaultMessage: 'el plazo para justificar venció' });
      case 'fecha_retorno_invalida':
        return intl.formatMessage({ id: 'disponibilidadActiva.masivo.omitido_fecha_retorno', defaultMessage: 'la fecha de retorno es anterior al inicio de la ociosidad' });
      default:
        return intl.formatMessage({ id: 'disponibilidadActiva.masivo.omitido_no_encontrado', defaultMessage: 'no se encontró el registro' });
    }
  };

  const informarResultado = ({ actualizados, omitidos }) => {
    const cantidad = actualizados.length;
    const titulo = cantidad > 0
      ? intl.formatMessage({
        id: 'disponibilidadActiva.masivo.exito',
        defaultMessage: '{cantidad, plural, one {Se justificó # registro con Éxito!} other {Se justificaron # registros con Éxito!}}',
      }, { cantidad })
      : intl.formatMessage({ id: 'disponibilidadActiva.masivo.ninguno', defaultMessage: 'No se justificó ningún registro' });

    if (omitidos.length === 0) {
      return swal2.fire(titulo, ' ', 'success');
    }

    const dominioPorId = {};
    seleccionados.forEach((m) => { dominioPorId[m.id] = m.dominio; });
    const lista = omitidos
      .map((o) => `<li>${escapeHtml(o.dominio || dominioPorId[o.id] || o.id)}: ${escapeHtml(textoOmitido(o.motivo))}</li>`)
      .join('');
    const encabezado = intl.formatMessage({ id: 'disponibilidadActiva.masivo.omitidos', defaultMessage: 'Se omitieron estos registros:' });

    return swal2.fire({
      title: titulo,
      html: `<p>${escapeHtml(encabezado)}</p><ul style="text-align:left">${lista}</ul>`,
      type: cantidad > 0 ? 'warning' : 'error',
    });
  };

  const handleConfirmar = () => {
    if (!motivoId || enviando) {
      return;
    }
    setEnviando(true);
    Utils.sendData('/disponibilidad/activa/motivo-masivo', 'PUT', {
      ids: seleccionados.map((m) => m.id),
      motivo_id: Number(motivoId),
      fecha_retorno: fechaRetorno ? fechaRetorno.format('YYYY-MM-DD') : null,
    })
      .then((resultado) => {
        cerrar();
        onJustificados();
        informarResultado(resultado);
      })
      .catch(() => {})
      .finally(() => setEnviando(false));
  };

  return (
    <Modal
      open={open}
      onClose={cerrar}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 1000 }}
      style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', marginTop: '10vh' }}
    >
      <Grow in={open}>
        <div className="modal-dialog" tabIndex="-1" id="justificar_masivo_modal" role="dialog" aria-labelledby="justificarMasivoLabel">
          <div className="modal-content">
            <div className="modal-header bg-fleet">
              <h4 className="modal-title text-white" id="justificarMasivoLabel">
                <FormattedMessage id="disponibilidadActiva.masivo.titulo" defaultMessage="Justificar registros seleccionados" />
              </h4>
              <button type="button" onClick={cerrar} className="close" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <p>
                <FormattedMessage
                  id="disponibilidadActiva.masivo.resumen"
                  defaultMessage="{registros, plural, one {<b>#</b> registro de ociosidad} other {<b>#</b> registros de ociosidad}} de {vehiculos, plural, one {<b>#</b> vehículo} other {<b>#</b> vehículos}} serán justificados con el mismo motivo."
                  values={{ registros: seleccionados.length, vehiculos, b: (chunks) => <strong>{chunks}</strong> }}
                />
              </p>
              <div className="mb-1">
                {dominios.map((dominio, i) => (
                  <span key={i} className="badge badge-pill bg-fleet text-white mr-1">{dominio}</span>
                ))}
              </div>
              <div className="form-group">
                <label htmlFor="justificarMasivoMotivo">
                  <FormattedMessage id="disponibilidadActiva.masivo.motivo" defaultMessage="Motivo" />*
                </label>
                <select
                  id="justificarMasivoMotivo"
                  className="form-control"
                  value={motivoId}
                  onChange={(e) => setMotivoId(e.target.value)}
                >
                  <option value="">
                    {intl.formatMessage({ id: 'disponibilidad_ociosidad_seleccioneMotivo', defaultMessage: 'Seleccione Motivo' })}
                  </option>
                  {comboMotivos.map((motivo) => (
                    <option key={motivo.id} value={motivo.id}>{motivo.nombre}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="justificarMasivoFechaRetorno">
                  <FormattedMessage id="disponibilidadActiva.col_fechaRetorno" defaultMessage="Fecha de Retorno" />
                </label>
                <div>
                  <DatePicker
                    id="justificarMasivoFechaRetorno"
                    className="form-control date-picker-placeholder"
                    placeholderText={intl.formatMessage({
                      id: 'movilesAbm.render.general_data.placeholder_fecha_de_alta',
                      defaultMessage: 'DD/MM/AAAA',
                    })}
                    selected={fechaRetorno}
                    onChange={setFechaRetorno}
                    minDate={inicioMasReciente ? moment(inicioMasReciente) : null}
                    onKeyDown={(e) => { if (e.key !== 'Tab') e.preventDefault(); }}
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    dateFormat={LocaleFormat.getDatePickerFormat()}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-danger" onClick={cerrar}>
                <i className="fa fa-times-circle mr-1" />
                <FormattedMessage id="Cancelar" defaultMessage="Cancelar" />
              </button>
              <button type="button" className="btn btn-fleet" disabled={!motivoId || enviando} onClick={handleConfirmar}>
                <i className="fa fa-check-circle mr-1" />
                <FormattedMessage id="disponibilidadActiva.masivo.confirmar" defaultMessage="Confirmar" />
              </button>
            </div>
          </div>
        </div>
      </Grow>
    </Modal>
  );
};

export default DisponibilidadActivaJustificarMasivoModal;
